import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Content,
  ContentHeader,
  Header,
  Page,
  Table,
  TableColumn,
  SupportButton,
  StatusOK,
  StatusError,
  StatusRunning,
  EmptyState,
  Progress,
} from '@backstage/core-components';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ClearAllIcon from '@material-ui/icons/ClearAll';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import Skeleton from '@material-ui/lab/Skeleton';
import { spassuCodeApiRef } from '../../api';
import {
  Activity,
  ActivityFilterOptions,
  ActivityFilters,
  ActivityStatus,
  PHASE_LABELS,
  STATUS_LABELS,
} from '../../api/types';
import { useProjectRole } from '../../hooks/useProjectRole';
import { activityDetailRouteRef } from '../../routes';
import { CreateActivityDialog } from '../CreateActivityDialog';
import { WorkerStatusIndicator } from '../WorkerStatusIndicator';
import {
  formatUserEntityRef,
  getCatalogEntityDisplayName,
} from '../../utils/format';
import { normalizeRouteRefPath } from '../../utils/routes';
import {
  resolveScopeKindFromProviders,
  scopeLabelsFor,
  ScopeKind,
} from '../../utils/scopeLabels';

const useStyles = makeStyles(theme => ({
  filters: {
    marginBottom: theme.spacing(2.5),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  chip: {
    marginRight: theme.spacing(0.5),
  },
  filterActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-start',
    alignItems: 'center',
    height: '100%',
  },
  clearButton: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    padding: theme.spacing(0.5, 1),
    minWidth: 'unset',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.primary,
    },
  },
  deleteButton: {
    fontSize: '0.78rem',
    color: theme.palette.error.main,
    padding: theme.spacing(0.5, 1),
    minWidth: 'unset',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.error.dark,
    },
  },
  loadingSkeleton: {
    marginTop: theme.spacing(1),
    display: 'grid',
    gap: theme.spacing(1),
  },
  actionInfo: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const StatusIndicator = ({
  status,
  errorMessage,
}: {
  status: ActivityStatus;
  errorMessage?: string | null;
}) => {
  switch (status) {
    case 'active':
      return <StatusRunning>{STATUS_LABELS.active}</StatusRunning>;
    case 'completed':
      return <StatusOK>{STATUS_LABELS.completed}</StatusOK>;
    case 'error': {
      const indicator = <StatusError>{STATUS_LABELS.error}</StatusError>;
      return errorMessage ? (
        <Tooltip title={errorMessage} arrow>
          <span>{indicator}</span>
        </Tooltip>
      ) : (
        indicator
      );
    }
    default:
      return <>{STATUS_LABELS[status]}</>;
  }
};

const PhaseChip = ({ phase }: { phase: string }) => {
  const label = PHASE_LABELS[phase as keyof typeof PHASE_LABELS] ?? phase;
  return <Chip size="small" label={label} variant="outlined" />;
};

export const ActivityListPage = () => {
  const classes = useStyles();
  const api = useApi(spassuCodeApiRef);
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const activityDetailRoute = useRouteRef(activityDetailRouteRef);
  const [currentUserRef, setCurrentUserRef] = useState<string | null>(null);
  // A listagem só carrega após resolver a identidade, porque o filtro padrão
  // "Criado por" é o próprio usuário.
  const [identityResolved, setIdentityResolved] = useState(false);
  const {
    roles: accessibleProjects,
    loading: rbacLoading,
    error: rbacError,
    hasPermission,
  } = useProjectRole();
  // Fallback to true when RBAC backend is unavailable (backwards compat)
  const canCreateActivity =
    rbacLoading || rbacError
      ? true
      : accessibleProjects.some(p =>
          hasPermission(p.projectKey, 'activity.create'),
        );

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();
  const [actionInfo, setActionInfo] = useState<string>();
  const [deletingIncomplete, setDeletingIncomplete] = useState(false);
  const [loadingFilterOptions, setLoadingFilterOptions] = useState(false);
  const [filterOptions, setFilterOptions] = useState<ActivityFilterOptions>({
    projects: [],
    repositories: [],
  });
  const [projectLabels, setProjectLabels] = useState<Record<string, string>>(
    {},
  );
  const [userLabels, setUserLabels] = useState<Record<string, string>>({});
  // "Projeto" (Azure DevOps) ou "Organização" (GitHub): o campo azureProject
  // guarda o repoOwner em providers flat, então só o rótulo muda.
  const [scopeKind, setScopeKind] = useState<ScopeKind>('project');
  const scopeLabels = scopeLabelsFor(scopeKind);
  const [filters, setFilters] = useState<ActivityFilters>({
    page: 1,
    limit: 20,
  });

  useEffect(() => {
    let cancelled = false;
    identityApi
      .getBackstageIdentity()
      .then(identity => {
        if (cancelled) return;
        setCurrentUserRef(identity.userEntityRef);
        setFilters(prev => ({ ...prev, createdBy: identity.userEntityRef }));
        setIdentityResolved(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCurrentUserRef(null);
        setIdentityResolved(true);
      });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const getProjectLabel = useCallback(
    (projectId: string) => projectLabels[projectId] ?? projectId,
    [projectLabels],
  );
  const getUserLabel = useCallback(
    (userId: string) => userLabels[userId] ?? formatUserEntityRef(userId),
    [userLabels],
  );

  const loadActivities = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const result = await api.listActivities(filters);
      setActivities(result.data);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    if (!identityResolved) return;
    loadActivities();
  }, [identityResolved, loadActivities]);

  const loadProjectLabels = useCallback(async () => {
    try {
      const projects = await api.listProjects();
      const nextLabels = Object.fromEntries(
        projects.map(project => [project.id, project.name]),
      );
      setProjectLabels(nextLabels);
    } catch {
      // Keep fallback labels when project lookup fails.
    }
  }, [api]);

  useEffect(() => {
    void loadProjectLabels();
  }, [loadProjectLabels]);

  useEffect(() => {
    let cancelled = false;
    api
      .listScmProviders()
      .then(providers => {
        if (!cancelled) setScopeKind(resolveScopeKindFromProviders(providers));
      })
      .catch(() => {
        // Keep "Projeto" when provider lookup fails.
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  const loadFilterOptions = useCallback(async () => {
    setLoadingFilterOptions(true);
    try {
      const result = await api.getActivityFilterOptions({
        status: filters.status,
        azureProject: filters.azureProject,
      });
      setFilterOptions(result);
    } catch {
      // Keep existing options when filter options endpoint fails.
    } finally {
      setLoadingFilterOptions(false);
    }
  }, [api, filters.azureProject, filters.status]);

  useEffect(() => {
    void loadFilterOptions();
  }, [loadFilterOptions]);

  const creatorOptions = useMemo(() => {
    const creators = new Set(filterOptions.creators ?? []);
    if (currentUserRef) creators.add(currentUserRef);
    return Array.from(creators);
  }, [currentUserRef, filterOptions.creators]);

  const catalogUserRefs = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...activities.map(activity => activity.userId),
            ...creatorOptions,
          ].filter(userId => Boolean(userId) && userId !== 'anonymous'),
        ),
      ),
    [activities, creatorOptions],
  );

  const sortedCreatorOptions = useMemo(
    () =>
      [...creatorOptions].sort((a, b) =>
        getUserLabel(a).localeCompare(getUserLabel(b), 'pt-BR'),
      ),
    [creatorOptions, getUserLabel],
  );

  useEffect(() => {
    if (catalogUserRefs.length === 0) {
      return undefined;
    }

    let cancelled = false;

    const loadCatalogUserLabels = async () => {
      try {
        const response = await catalogApi.getEntitiesByRefs({
          entityRefs: catalogUserRefs,
          fields: [
            'metadata.name',
            'metadata.title',
            'spec.profile.displayName',
          ],
        });

        if (cancelled) {
          return;
        }

        const nextLabels: Record<string, string> = {};
        catalogUserRefs.forEach((userRef, index) => {
          const label = getCatalogEntityDisplayName(response.items[index]);
          if (label) {
            nextLabels[userRef] = label;
          }
        });

        if (Object.keys(nextLabels).length > 0) {
          setUserLabels(prev => ({
            ...prev,
            ...nextLabels,
          }));
        }
      } catch {
        // Keep fallback labels when catalog lookup fails.
      }
    };

    void loadCatalogUserLabels();
    return () => {
      cancelled = true;
    };
  }, [catalogApi, catalogUserRefs]);

  useEffect(() => {
    if (
      filters.azureProject &&
      !filterOptions.projects.includes(filters.azureProject)
    ) {
      setFilters(prev => ({
        ...prev,
        azureProject: undefined,
        azureRepository: undefined,
        page: 1,
      }));
      return;
    }

    if (
      filters.azureRepository &&
      !filterOptions.repositories.includes(filters.azureRepository)
    ) {
      setFilters(prev => ({
        ...prev,
        azureRepository: undefined,
        page: 1,
      }));
    }
  }, [
    filterOptions.projects,
    filterOptions.repositories,
    filters.azureProject,
    filters.azureRepository,
  ]);

  const handleResetFilters = useCallback(() => {
    setFilters(prev => ({
      page: 1,
      limit: prev.limit ?? 20,
      createdBy: currentUserRef ?? undefined,
    }));
  }, [currentUserRef]);

  const isActivityLockedForUser = useCallback(
    (activity: Activity) => {
      if (currentUserRef === null) return false;
      if (activity.userId === currentUserRef) return false;
      if (activity.status === 'completed') return false;
      if (hasPermission(activity.azureProject, 'activity.bypass_owner_lock')) {
        return false;
      }
      return true;
    },
    [currentUserRef, hasPermission],
  );

  const handleDeleteIncomplete = useCallback(async () => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      'Excluir todas as atividades não concluídas (preparando, ativas e com erro)?',
    );
    if (!confirmed) {
      return;
    }

    setDeletingIncomplete(true);
    setError(undefined);
    setActionInfo(undefined);
    try {
      const result = await api.deleteIncompleteActivities();
      setActionInfo(
        `${result.deletedCount} atividade(s) não concluída(s) removida(s).`,
      );
      await Promise.all([loadActivities(), loadFilterOptions()]);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setDeletingIncomplete(false);
    }
  }, [api, loadActivities, loadFilterOptions]);

  const getPrimaryActivityRoute = useCallback(
    (activity: Activity) =>
      normalizeRouteRefPath(activityDetailRoute({ activityId: activity.id })),
    [activityDetailRoute],
  );

  const columns: TableColumn<Activity>[] = [
    {
      title: 'Título',
      field: 'title',
      highlight: true,
      render: row => {
        if (isActivityLockedForUser(row)) {
          return (
            <Tooltip
              title="Atividade pertence a outro usuário. Acesso liberado apenas após a conclusão."
              arrow
            >
              <span
                style={{
                  opacity: 0.55,
                  cursor: 'not-allowed',
                  color: 'inherit',
                }}
              >
                {row.title}
              </span>
            </Tooltip>
          );
        }
        return (
          <RouterLink to={getPrimaryActivityRoute(row)}>{row.title}</RouterLink>
        );
      },
    },
    {
      title: 'Issue',
      field: 'azureWorkItem' as any,
      width: '80px',
      render: row => {
        if (row.azureWorkItem) {
          return (
            <Tooltip
              title={`${row.azureWorkItem.type}: ${row.azureWorkItem.title}`}
              arrow
            >
              <a
                href={row.azureWorkItem.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  fontWeight: 600,
                  color: '#FA4616',
                  textDecoration: 'none',
                }}
              >
                #{row.azureWorkItem.id}
              </a>
            </Tooltip>
          );
        }
        if (row.issue) {
          return (
            <Tooltip
              title={`${row.issue.type ? `${row.issue.type}: ` : ''}${
                row.issue.title
              }`}
              arrow
            >
              <a
                href={row.issue.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{
                  fontWeight: 600,
                  color: '#FA4616',
                  textDecoration: 'none',
                }}
              >
                #{row.issue.externalId}
              </a>
            </Tooltip>
          );
        }
        return <span style={{ color: '#ccc' }}>—</span>;
      },
    },
    {
      title: 'Status',
      field: 'status',
      render: row => (
        <StatusIndicator status={row.status} errorMessage={row.errorMessage} />
      ),
    },
    {
      title: 'Provider',
      field: 'provider' as any,
      width: '100px',
      render: (row: Activity) => (
        <Chip
          size="small"
          variant="outlined"
          label={
            (
              {
                'azure-devops': 'Azure DevOps',
                github: 'GitHub',
                gitlab: 'GitLab',
                'bitbucket-cloud': 'Bitbucket',
                'bitbucket-server': 'Bitbucket Server',
                gitea: 'Gitea',
              } as Record<string, string>
            )[row.provider ?? 'azure-devops'] ??
            row.provider ??
            'Azure DevOps'
          }
        />
      ),
    } as TableColumn<Activity>,
    {
      title: 'Fase Atual',
      field: 'currentPhase',
      render: row => <PhaseChip phase={row.currentPhase} />,
    },
    {
      title: 'Criado por',
      field: 'userId',
      render: row => getUserLabel(row.userId),
    },
    {
      title: 'Repositório',
      field: 'azureRepository',
    },
    {
      title: scopeLabels.singular,
      field: 'azureProject',
      render: row => getProjectLabel(row.azureProject),
    },
    {
      title: 'Criado em',
      field: 'createdAt',
      render: row => new Date(row.createdAt).toLocaleDateString('pt-BR'),
    },
  ];

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Frontier" subtitle="Desenvolvimento assistido por IA">
          <WorkerStatusIndicator />
        </Header>
        <Content>
          <EmptyState
            title="Erro ao carregar atividades"
            description={error.message}
            missing="info"
            action={
              <Button
                variant="contained"
                color="primary"
                onClick={loadActivities}
              >
                Tentar novamente
              </Button>
            }
          />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Header title="Frontier" subtitle="Desenvolvimento assistido por IA">
        <WorkerStatusIndicator />
      </Header>
      <Content>
        <ContentHeader title="Atividades">
          <SupportButton>
            Plugin para desenvolvimento assistido por IA com pipeline de 7
            fases.
          </SupportButton>
          {canCreateActivity && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Nova Atividade
            </Button>
          )}
        </ContentHeader>

        <CreateActivityDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCreated={loadActivities}
        />

        <Grid container spacing={2} className={classes.filters}>
          <Grid item xs={12} sm={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-status-label">Status</InputLabel>
              <Select
                labelId="filter-status-label"
                label="Status"
                value={filters.status ?? ''}
                onChange={e => {
                  const value = e.target.value as string;
                  setFilters(prev => ({
                    ...prev,
                    status: value ? (value as ActivityStatus) : undefined,
                    page: 1,
                  }));
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="preparing">Preparando</MenuItem>
                <MenuItem value="active">Ativa</MenuItem>
                <MenuItem value="completed">Concluída</MenuItem>
                <MenuItem value="error">Erro</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-creator-label">Criado por</InputLabel>
              <Select
                labelId="filter-creator-label"
                label="Criado por"
                value={filters.createdBy ?? ''}
                onChange={e => {
                  const value = e.target.value as string;
                  setFilters(prev => ({
                    ...prev,
                    createdBy: value || undefined,
                    page: 1,
                  }));
                }}
              >
                <MenuItem value="">Todos</MenuItem>
                {sortedCreatorOptions.map(userRef => (
                  <MenuItem key={userRef} value={userRef}>
                    {getUserLabel(userRef)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-project-label">
                {scopeLabels.singular}
              </InputLabel>
              <Select
                labelId="filter-project-label"
                label={scopeLabels.singular}
                value={filters.azureProject ?? ''}
                disabled={
                  loadingFilterOptions && filterOptions.projects.length === 0
                }
                onChange={e => {
                  const value = e.target.value as string;
                  setFilters(prev => ({
                    ...prev,
                    azureProject: value || undefined,
                    azureRepository: undefined,
                    page: 1,
                  }));
                }}
              >
                <MenuItem value="">
                  {scopeKind === 'owner' ? 'Todas' : 'Todos'}
                </MenuItem>
                {filterOptions.projects.map(projectId => (
                  <MenuItem key={projectId} value={projectId}>
                    {getProjectLabel(projectId)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="filter-repo-label">Repositório</InputLabel>
              <Select
                labelId="filter-repo-label"
                label="Repositório"
                value={filters.azureRepository ?? ''}
                onChange={e => {
                  const value = e.target.value as string;
                  setFilters(prev => ({
                    ...prev,
                    azureRepository: value || undefined,
                    page: 1,
                  }));
                }}
                disabled={
                  loadingFilterOptions ||
                  filterOptions.repositories.length === 0
                }
              >
                <MenuItem value="">Todos</MenuItem>
                {filterOptions.repositories.map(repository => (
                  <MenuItem key={repository} value={repository}>
                    {repository}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <div className={classes.filterActions}>
              <Button
                size="small"
                startIcon={<ClearAllIcon fontSize="small" />}
                onClick={handleResetFilters}
                disabled={loading || deletingIncomplete}
                className={classes.clearButton}
                aria-label="Limpar filtros"
              >
                Limpar
              </Button>
              <Button
                size="small"
                startIcon={<DeleteSweepIcon fontSize="small" />}
                onClick={handleDeleteIncomplete}
                disabled={loading || deletingIncomplete}
                className={classes.deleteButton}
                aria-label="Excluir atividades não concluídas"
              >
                {deletingIncomplete ? 'Limpando...' : 'Excluir'}
              </Button>
            </div>
          </Grid>
        </Grid>

        {actionInfo && (
          <Typography variant="body2" className={classes.actionInfo}>
            {actionInfo}
          </Typography>
        )}

        {loading && (
          <>
            <Progress />
            <div
              className={classes.loadingSkeleton}
              aria-label="Carregando atividades"
            >
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
              <Skeleton variant="rect" height={36} />
            </div>
          </>
        )}

        {!loading && activities.length === 0 && !error && (
          <EmptyState
            title="Nenhuma atividade encontrada"
            description="Crie uma nova atividade para começar a usar o Frontier."
            missing="content"
            action={
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setDialogOpen(true)}
              >
                Nova Atividade
              </Button>
            }
          />
        )}

        {!loading && activities.length > 0 && (
          <Table
            title={`${total} atividade${total !== 1 ? 's' : ''}`}
            columns={columns}
            data={activities}
            options={{
              paging: true,
              pageSize: filters.limit ?? 20,
              search: false,
              padding: 'dense',
            }}
            page={(filters.page ?? 1) - 1}
            totalCount={total}
            onPageChange={page => {
              setFilters(prev => ({ ...prev, page: page + 1 }));
            }}
            onRowsPerPageChange={pageSize => {
              setFilters(prev => ({ ...prev, limit: pageSize, page: 1 }));
            }}
          />
        )}
      </Content>
    </Page>
  );
};
